import React, { createContext, useContext } from 'react';

import { Box, Tooltip } from '@mui/material';

import calibration from './confidenceCalibration.json';
import { aggregateConfidenceTooltip } from './confidenceText';
import { emptyPredictionTooltip } from './emptyPredictionText';
import type { agg } from './logic';
import { ConfidenceTier } from './types';

export const StalePredictionContext = createContext<string | null>(null);

export const confidenceLabels: Record<ConfidenceTier, string> = {
  high: '●●● High',
  medium: '●●○ Medium',
  low: '●○○ Low',
  indicative: '○○○ Indicative',
};

export default function ConfidenceBadge({
  tier = 'indicative',
  capacity,
  source,
  score,
  aggregate,
  mix,
  final = false,
  tabIndex = 0,
}: {
  tier?: ConfidenceTier | null;
  capacity?: number | null;
  source?: string | null;
  score?: number | null;
  aggregate?: boolean;
  mix?: ReturnType<typeof agg>['confidenceMix'];
  final?: boolean;
  tabIndex?: number;
}) {
  const staleTooltip = useContext(StalePredictionContext);
  if (tier === null && !final) return null;
  if (final) tier = 'indicative';
  if (staleTooltip || source === 'zero_model') {
    tier = 'indicative';
  }
  const rate = tier === 'indicative' ? null : calibration.actualRates[tier];
  const tolerance = Math.max(2, (capacity || 0) * 0.1);
  const title =
    (final && 'At or past handback: these numbers are final.') ||
    staleTooltip ||
    (aggregate
      ? aggregateConfidenceTooltip(mix || [])
      : source === 'zero_model'
        ? emptyPredictionTooltip
        : tier === 'indicative'
          ? 'Early estimate from tour history and booking signals. Good for spotting slow sellers, not for exact numbers.'
          : source === 'pace'
            ? `Pace estimate: ${Math.round(score || 0)}% of similar held-out predictions finished within ±${tolerance.toLocaleString('en-AU')} travellers. Pace confidence is capped at Medium.`
            : `${tier.charAt(0).toUpperCase()}${tier.slice(1)} confidence: about ${Math.round((rate || 0) * 10)} in 10 predictions like this finish within ±${tolerance.toLocaleString('en-AU')} travellers. Measured on ${calibration.testQuarter}.`);
  return (
    <Tooltip
      title={
        <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
          {title}
        </Box>
      }
    >
      <Box
        component="span"
        data-confidence-pill
        tabIndex={tabIndex}
        sx={{
          display: 'inline-block',
          borderRadius: 8,
          bgcolor:
            tier === 'high'
              ? '#17636a'
              : tier === 'medium'
                ? 'rgba(66,125,182,.2)'
                : tier === 'low'
                  ? 'rgba(110,140,165,.12)'
                  : 'transparent',
          border: tier === 'indicative' ? '1px dashed #898781' : '1px solid transparent',
          px: 0.75,
          py: 0.4,
          fontSize: 13,
          fontWeight: 600,
          color: tier === 'high' ? '#fff' : tier === 'medium' ? 'var(--tcc-predicted-text, #326799)' : 'text.secondary',
          whiteSpace: 'nowrap',
          fontStyle: tier === 'indicative' ? 'italic' : 'normal',
        }}
      >
        {final ? 'Final' : confidenceLabels[tier]}
      </Box>
    </Tooltip>
  );
}
